import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft, 
    HeartHandshake, 
    MessageCircle, 
    Pause, 
    Link, 
    Shield, 
    Heart,
    ChevronRight,
    Copy,
    Check,
    ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, SecondaryButton, IconButton } from '../components/brand/BrandButton';
import { SelectionCard, ScriptCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Badge } from '../components/brand/BrandIndicators';
import { Container } from '../components/brand/BrandLayout';

const iconMap = {
    'heart-handshake': HeartHandshake,
    'message-circle': MessageCircle,
    'pause': Pause,
    'link': Link,
    'shield': Shield,
    'hand-heart': Heart
};

const ruptureLabels = {
    mild: 'Gentle moment',
    moderate: 'Difficult moment',
    severe: 'Intense moment'
};

// Glass card variant styles - matching Regulation Hub design
const variantStyles = {
    sage: { glassClass: 'glass-card-sage', iconGlow: 'icon-glow-sage' },
    lavender: { glassClass: 'glass-card-lavender', iconGlow: 'icon-glow-lavender' },
    rose: { glassClass: 'glass-card-rose', iconGlow: 'icon-glow-rose' },
    blue: { glassClass: 'glass-card-blue', iconGlow: 'icon-glow-blue' },
    gold: { glassClass: 'glass-card-gold', iconGlow: 'icon-glow-gold' }
};

// Variant assignments for intents (cycles through variants)
const intentVariants = ['sage', 'lavender', 'rose', 'blue', 'gold'];

const Communicate = () => {
    const navigate = useNavigate();
    const { api } = useAuth();
    
    const [step, setStep] = useState('intent');
    const [intents, setIntents] = useState([]);
    const [selectedIntent, setSelectedIntent] = useState(null);
    const [selectedRupture, setSelectedRupture] = useState(null);
    const [scripts, setScripts] = useState([]);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchIntents = async () => {
            try {
                const response = await api().get('/communication/intents');
                setIntents(response.data);
            } catch (error) {
                toast.error('Unable to load options');
            }
        };
        fetchIntents();
    }, [api]);

    const handleSelectIntent = (intent) => {
        setSelectedIntent(intent);
        setStep('rupture');
    };

    const handleSelectRupture = async (level) => {
        setSelectedRupture(level);
        setLoading(true);
        
        try {
            const response = await api().get(`/communication/scripts/${selectedIntent.id}/${level}`);
            setScripts(response.data);
            setStep('scripts');
        } catch (error) {
            toast.error('Unable to load scripts');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyScript = async (script, index) => {
        try {
            await navigator.clipboard.writeText(script);
            setCopiedIndex(index);
            toast.success('Copied to clipboard');
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch (error) {
            toast.error('Unable to copy');
        }
    };

    const handleBack = () => {
        if (step === 'scripts') {
            setStep('rupture');
            setScripts([]);
        } else if (step === 'rupture') {
            setStep('intent');
            setSelectedIntent(null);
        } else {
            navigate('/relationship-hub');
        }
    };

    const handleStartOver = () => {
        setStep('intent');
        setSelectedIntent(null);
        setSelectedRupture(null);
        setScripts([]);
    };

    // Select communication intent screen
    if (step === 'intent') {
        return (
            <div className="min-h-screen bg-transparent">
                <header className="sticky top-0 z-50 glass-header">
                    <Container size="sm" className="py-5 flex items-center justify-between">
                        <button 
                            onClick={() => navigate('/relationship-hub')}
                            className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/40 transition-colors text-[#7D8E95] hover:text-[#1D6B6B]"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <Heading as="h1" size="text-lg" className="text-[#1D6B6B]">Communication</Heading>
                        <div className="w-11" />
                    </Container>
                </header>

                <main className="max-w-2xl mx-auto px-6 py-10">
                    <div className="text-center mb-10 brand-fade-in">
                        <Heading as="h2" className="mb-4 text-[#1D6B6B]">
                            What do you need to express?
                        </Heading>
                        <BodyText size="lg" className="text-[#2A8585]">
                            Select the kind of communication you're looking for.
                        </BodyText>
                    </div>

                    <div className="space-y-4">
                        {intents.map((intent, index) => {
                            const IconComponent = iconMap[intent.icon] || MessageCircle;
                            const variant = intentVariants[index % intentVariants.length];
                            const style = variantStyles[variant];
                            return (
                                <button 
                                    key={intent.id}
                                    className={`w-full ${style.glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine brand-slide-up
                                             flex items-center gap-5`}
                                    style={{ animationDelay: `${index * 60}ms` }}
                                    onClick={() => handleSelectIntent(intent)}
                                    data-testid={`intent-${intent.id}`}
                                >
                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-[#2A8585] via-[#1D6B6B] to-[#145555] flex items-center justify-center flex-shrink-0 ${style.iconGlow}`}>
                                        <IconComponent className="w-6 h-6 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <Heading as="h3" size="text-lg" className="mb-0.5 text-[#1D6B6B] font-semibold">
                                            {intent.name}
                                        </Heading>
                                        <BodyText className="text-[#2A8585] text-sm">
                                            {intent.description}
                                        </BodyText>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                                </button>
                            );
                        })}
                    </div>
                </main>
            </div>
        );
    }

    // Select rupture level screen
    if (step === 'rupture') {
        const ruptureOptions = [
            { level: 'mild', description: 'A small misunderstanding or minor disconnect', variant: 'sage' },
            { level: 'moderate', description: 'Tension that needs addressing but feels manageable', variant: 'lavender' },
            { level: 'severe', description: 'Significant distress that needs careful attention', variant: 'rose' }
        ];

        return (
            <div className="min-h-screen bg-transparent">
                <header className="sticky top-0 z-50 glass-header">
                    <Container size="sm" className="py-5 flex items-center justify-between">
                        <button 
                            onClick={handleBack}
                            className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/40 transition-colors text-[#7D8E95] hover:text-[#1D6B6B]"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <Heading as="h1" size="text-lg" className="text-[#1D6B6B]">{selectedIntent?.name}</Heading>
                        <div className="w-11" />
                    </Container>
                </header>

                <main className="max-w-2xl mx-auto px-6 py-10">
                    <div className="text-center mb-10 brand-fade-in">
                        <Heading as="h2" className="mb-4 text-[#1D6B6B]">
                            How intense is this moment?
                        </Heading>
                        <BodyText size="lg" className="text-[#2A8585]">
                            This helps us find the right words for where you are.
                        </BodyText>
                    </div>

                    <div className="space-y-4">
                        {ruptureOptions.map((option, index) => {
                            const style = variantStyles[option.variant];
                            return (
                                <button 
                                    key={option.level}
                                    className={`w-full ${style.glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine brand-slide-up
                                             ${loading ? 'opacity-50 pointer-events-none' : ''}`}
                                    style={{ animationDelay: `${index * 60}ms` }}
                                    onClick={() => handleSelectRupture(option.level)}
                                    data-testid={`rupture-${option.level}`}
                                >
                                    <Heading as="h3" size="text-lg" className="mb-1 text-[#1D6B6B] font-semibold">
                                        {ruptureLabels[option.level]}
                                    </Heading>
                                    <BodyText className="text-[#2A8585] text-sm">
                                        {option.description}
                                    </BodyText>
                                </button>
                            );
                        })}
                    </div>
                </main>
            </div>
        );
    }

    // Scripts screen
    if (step === 'scripts') {
        return (
            <div className="min-h-screen bg-transparent">
                <header className="sticky top-0 z-50 glass-header">
                    <Container size="sm" className="py-5 flex items-center justify-between">
                        <button 
                            onClick={handleBack}
                            className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/40 transition-colors text-[#7D8E95] hover:text-[#1D6B6B]"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <Heading as="h1" size="text-lg" className="text-[#1D6B6B]">Words for You</Heading>
                        <div className="w-11" />
                    </Container>
                </header>

                <main className="max-w-2xl mx-auto px-6 py-10">
                    <div className="text-center mb-10 brand-fade-in">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D6B6B]/10 mb-5">
                            <span className="text-sm font-medium text-[#1D6B6B]">
                                {selectedIntent?.name} • {ruptureLabels[selectedRupture]}
                            </span>
                        </div>
                        <Heading as="h2" className="mb-4 text-[#1D6B6B]">
                            Choose what feels right
                        </Heading>
                        <BodyText size="lg" className="text-[#2A8585]">
                            Tap to copy. Modify as needed.
                        </BodyText>
                    </div>

                    <div className="space-y-4">
                        {scripts.map((script, index) => {
                            const variant = intentVariants[index % intentVariants.length];
                            const style = variantStyles[variant];
                            return (
                                <button 
                                    key={index}
                                    className={`w-full ${style.glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine brand-slide-up text-left`}
                                    style={{ animationDelay: `${index * 60}ms` }}
                                    onClick={() => handleCopyScript(script, index)}
                                    data-testid={`script-${index}`}
                                >
                                    <BodyText size="lg" className="text-[#1D6B6B] mb-4 italic leading-relaxed">
                                        "{script}"
                                    </BodyText>
                                    <div className="flex items-center justify-end">
                                        <span 
                                            className="flex items-center gap-2 text-sm text-[#1D6B6B] font-semibold"
                                            data-testid={`copy-script-${index}`}
                                        >
                                            {copiedIndex === index ? (
                                                <>
                                                    <Check className="w-4 h-4" />
                                                    <span>Copied</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="w-4 h-4" />
                                                    <span>Copy</span>
                                                </>
                                            )}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-10 space-y-3">
                        <button 
                            onClick={handleStartOver}
                            className="w-full py-4 px-6 rounded-2xl glass-card-lavender text-[#1D6B6B] font-semibold
                                     card-hover hover-shine transition-all duration-200"
                            data-testid="different-words-btn"
                        >
                            Find Different Words
                        </button>
                        <button 
                            onClick={() => navigate('/relationship-hub')}
                            className="w-full py-4 px-6 rounded-2xl bg-[#1D6B6B] text-white font-semibold
                                     hover:bg-[#145555] transition-all shadow-md flex items-center justify-center gap-2"
                            data-testid="back-to-hub-btn"
                        >
                            <span>Back to Relationship Hub</span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    return null;
};

export default Communicate;
